import { Controller } from "@hotwired/stimulus"

/**
 * PricingTableController
 *
 * Listens for Redux cart state changes and refreshes the pricing table
 * Turbo Frame when pickup/return dates change.
 *
 * Usage:
 *   <div data-controller="pricing-table"
 *        data-pricing-table-product-id-value="123"
 *        data-pricing-table-url-value="/product/123/pricing_table">
 *     <turbo-frame id="pricing-table-123">
 *       ...
 *     </turbo-frame>
 *   </div>
 */
export default class extends Controller {
  static values = {
    productId: Number,
    url: String
  }

  connect() {
    this.previousPickupTime = null
    this.previousReturnTime = null
    this.unsubscribe = null

    if (!window.reduxStore) {
      console.warn('[PricingTableController] Redux store not available')
      return
    }

    this.syncCartState()
    this.unsubscribe = window.reduxStore.subscribe(() => this.handleStoreChange())
  }

  disconnect() {
    if (this.unsubscribe) {
      this.unsubscribe()
      this.unsubscribe = null
    }
  }

  syncCartState() {
    const state = window.reduxStore.getState()
    this.previousPickupTime = state.cart?.pickup_time
    this.previousReturnTime = state.cart?.return_time
  }

  handleStoreChange() {
    const state = window.reduxStore.getState()
    const currentPickupTime = state.cart?.pickup_time
    const currentReturnTime = state.cart?.return_time

    if (this.hasCartDatesChanged(currentPickupTime, currentReturnTime)) {
      this.previousPickupTime = currentPickupTime
      this.previousReturnTime = currentReturnTime

      if (this.areDatesValid(currentPickupTime, currentReturnTime)) {
        this.refreshPricingTable()
      }
    }
  }

  hasCartDatesChanged(currentPickupTime, currentReturnTime) {
    return (
      currentPickupTime !== this.previousPickupTime ||
      currentReturnTime !== this.previousReturnTime
    )
  }

  areDatesValid(pickupTime, returnTime) {
    return pickupTime && returnTime
  }

  refreshPricingTable() {
    const frame = this.element.querySelector(`turbo-frame#pricing-table-${this.productIdValue}`)

    if (frame) {
      frame.src = this.urlValue
      frame.reload()
    }
  }
}
